(() => {
  function initTesterBuilder(section) {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const sectionId = section.dataset.sectionId;
    const grid = section.querySelector(`#testerGrid-${sectionId}`);
    const search = section.querySelector(`#testerSearch-${sectionId}`);
    const filters = section.querySelector(`#testerBrandFilters-${sectionId}`);
    const status = section.querySelector(`#testerStatus-${sectionId}`);
    const empty = section.querySelector(`#testerEmpty-${sectionId}`);
    const loadWrap = section.querySelector(`#testerLoadMoreWrap-${sectionId}`);
    const loadButton = section.querySelector(`#testerLoadMore-${sectionId}`);
    const loadText = section.querySelector(`#testerLoadMoreLoading-${sectionId}`);
    const countEl = section.querySelector(`#selectedCount-${sectionId}`);
    const totalEl = section.querySelector(`#selectedPrice-${sectionId}`);
    const addButton = section.querySelector(`#testerAddToCart-${sectionId}`);
    const sizeButtons = section.querySelectorAll('.sa-size-button');
    const minimum = 1; // ScentAdda requirement: one tester is enough to enable Add to Cart.
    const pageSize = Math.max(1, Number(section.dataset.pageSize) || 50);
    const collectionHandles = { '2ml': section.dataset.collectionTwoMl, '5ml': section.dataset.collectionFiveMl };

    let activeSize = '2ml';
    let activeBrand = 'all';
    let productCache = [];
    let visibleCount = 0;
    let apiPage = 1;
    let hasMoreApiPages = true;
    let isLoadingMore = false;
    let requestToken = 0;
    let allProductsLoaded = false;

    const money = (amount) => {
      const currency = section.dataset.currency || window.Shopify?.currency?.active || 'INR';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency }).format(amount); }
      catch (_) { return `₹${Number(amount).toFixed(2)}`; }
    };
    const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
    const handleize = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const selectedQty = (card) => Number(card.querySelector('.qty')?.textContent || 0);
    const getCards = () => Array.from(grid.querySelectorAll('.sa-card'));

    function resetSelectedProducts() {
      getCards().forEach(card => { const qty = card.querySelector('.qty'); if (qty) qty.textContent = '0'; card.classList.remove('selected'); });
      updateSummary();
    }

    function cardMarkup(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find(v => v.available) || variants[0];
      if (!variant) return '';
      const rawImage = product.featured_image || product.image || product.images?.[0] || '';
      const imageValue = (value) => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') return value.src || value.url || value.original_src || '';
        return '';
      };
      let image = imageValue(rawImage);
      if (image.startsWith('//')) image = `${window.location.protocol}${image}`;
      else if (image.startsWith('/')) image = `${window.location.origin}${image}`;
      const price = Math.round(Number(variant.price ?? product.price ?? 0) * 100);
      const vendor = product.vendor || 'Fragrance';
      const title = product.title || 'Tester';
      const url = product.url || `/products/${product.handle}`;
      return `<article class="sa-card${variant.available ? '' : ' sa-out-of-stock'}" data-title="${escapeHTML(title.toLowerCase())}" data-brand="${escapeHTML(handleize(vendor))}" data-price="${price}" data-variant="${escapeHTML(variant.id)}" data-url="${escapeHTML(url)}" data-available="${variant.available ? 'true' : 'false'}">
        <div class="sa-image">${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(product.title || title)}" loading="lazy" width="600" height="600">` : '<div class="sa-image-placeholder">No Image</div>'}${variant.available ? '' : '<span class="sa-stock-badge">Out of Stock</span>'}</div>
        <div class="sa-info"><span class="sa-brand">${escapeHTML(vendor)}</span><h3>${escapeHTML(title)}</h3><div class="sa-size">${activeSize} Tester</div><div class="sa-price">${money(price / 100)}</div></div>
        <div class="sa-qty"><button type="button" class="minus" aria-label="Decrease quantity" ${variant.available ? '' : 'disabled'}>-</button><span class="qty" aria-live="polite">0</span><button type="button" class="plus" aria-label="Increase quantity" ${variant.available ? '' : 'disabled'}>+</button></div>
      </article>`;
    }

    function renderBrandFilters() {
      if (!filters) return;
      const brands = [...new Set(productCache.map(p => p.vendor).filter(Boolean))];
      filters.innerHTML = `<button type="button" data-brand="all" class="${activeBrand === 'all' ? 'active' : ''}" aria-pressed="${activeBrand === 'all'}">All</button>` + brands.map(brand => {
        const key = handleize(brand);
        return `<button type="button" data-brand="${escapeHTML(key)}" class="${activeBrand === key ? 'active' : ''}" aria-pressed="${activeBrand === key}">${escapeHTML(brand)}</button>`;
      }).join('');
    }

    function applyFilters() {
      const keyword = (search?.value || '').trim().toLowerCase();
      let matches = 0;
      getCards().forEach(card => {
        const visible = (card.dataset.title || '').includes(keyword) && (activeBrand === 'all' || card.dataset.brand === activeBrand);
        card.hidden = !visible;
        if (visible) matches++;
      });
      empty.hidden = matches !== 0;
      updateSummary();
    }

    function updateSummary() {
      let qtyTotal = 0;
      let priceTotal = 0;
      getCards().forEach(card => {
        const qty = selectedQty(card);
        const available = card.dataset.available !== 'false';
        card.classList.toggle('selected', qty > 0 && available);
        if (available && qty > 0) { qtyTotal += qty; priceTotal += qty * (Number(card.dataset.price) || 0); }
      });
      countEl.textContent = String(qtyTotal);
      totalEl.textContent = money(priceTotal / 100);
      addButton.disabled = qtyTotal < minimum;
      addButton.textContent = qtyTotal === 0 ? 'Select Tester' : qtyTotal < minimum ? `Select ${minimum - qtyTotal} more` : `Add ${qtyTotal} Testers`;
    }

    function setupCard(card) {
      card.querySelector('.sa-image img')?.addEventListener('error', event => {
        const img = event.currentTarget;
        img.style.display = 'none';
        const holder = img.closest('.sa-image');
        if (holder && !holder.querySelector('.sa-image-placeholder')) {
          const placeholder = document.createElement('div');
          placeholder.className = 'sa-image-placeholder';
          placeholder.textContent = 'Image unavailable';
          holder.prepend(placeholder);
        }
      }, { once: true });
      card.querySelector('.plus')?.addEventListener('click', event => {
        event.stopPropagation();
        if (card.dataset.available === 'false') return;
        const qty = card.querySelector('.qty');
        qty.textContent = String(selectedQty(card) + 1);
        updateSummary();
      });
      card.querySelector('.minus')?.addEventListener('click', event => {
        event.stopPropagation();
        if (card.dataset.available === 'false') return;
        const qty = card.querySelector('.qty');
        qty.textContent = String(Math.max(0, selectedQty(card) - 1));
        updateSummary();
      });
      card.addEventListener('click', event => {
        if (event.target.closest('.plus, .minus')) return;
        if (card.dataset.url) window.location.href = card.dataset.url;
      });
    }

    function renderNextChunk() {
      const nextProducts = productCache.slice(visibleCount, visibleCount + pageSize);
      if (!nextProducts.length) return 0;
      const html = nextProducts.map(cardMarkup).join('');
      grid.insertAdjacentHTML('beforeend', html);
      const newCards = getCards().slice(-nextProducts.length);
      newCards.forEach(setupCard);
      visibleCount += nextProducts.length;
      applyFilters();
      return nextProducts.length;
    }

    async function fetchApiPage(handle, page, token) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Unable to load collection products (${response.status}).`);
      const data = await response.json();
      if (token !== requestToken) return [];
      const products = Array.isArray(data.products) ? data.products : [];
      if (products.length < 250) hasMoreApiPages = false;
      return products.map(p => ({ ...p, url: `${root}products/${p.handle}` }));
    }

    async function loadInitial(size) {
      const token = ++requestToken;
      activeSize = size;
      activeBrand = 'all';
      productCache = [];
      visibleCount = 0;
      apiPage = 1;
      hasMoreApiPages = true;
      allProductsLoaded = false;
      isLoadingMore = false;
      grid.innerHTML = '';
      grid.setAttribute('aria-busy', 'true');
      loadWrap.hidden = true;
      empty.hidden = true;
      status.hidden = false;
      status.textContent = 'Loading testers…';
      if (search) search.value = '';
      resetSelectedProducts();
      sizeButtons.forEach(button => {
        const active = button.dataset.size === size;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const handle = collectionHandles[size];
      if (!handle) {
        status.textContent = `Please select the ${size} tester collection in the Shopify Theme Editor.`;
        grid.setAttribute('aria-busy', 'false');
        return;
      }
      try {
        const products = await fetchApiPage(handle, 1, token);
        if (token !== requestToken) return;
        productCache = products;
        apiPage = 1;
        renderBrandFilters();
        renderNextChunk();
        status.hidden = true;
        grid.setAttribute('aria-busy', 'false');
        if (!productCache.length) { empty.hidden = false; }
        updateLoadMoreVisibility();
      } catch (error) {
        if (token !== requestToken) return;
        status.textContent = 'Could not load testers. Please refresh the page and try again.';
        console.error('[ScentAdda Tester Builder]', error);
        grid.setAttribute('aria-busy', 'false');
      }
    }

    function updateLoadMoreVisibility() {
      const hasCached = visibleCount < productCache.length;
      const canFetchMore = hasMoreApiPages && productCache.length > 0 && productCache.length % 250 === 0;
      loadWrap.hidden = !(hasCached || canFetchMore);
      if (!loadWrap.hidden) {
        loadButton.disabled = false;
        loadButton.textContent = 'View More';
      }
    }

    async function loadMoreProducts() {
      if (isLoadingMore || loadWrap.hidden) return;
      isLoadingMore = true;
      loadButton.disabled = true;
      loadButton.classList.add('loading');
      loadText.hidden = false;
      const token = requestToken;
      try {
        if (visibleCount >= productCache.length && hasMoreApiPages && productCache.length % 250 === 0) {
          const handle = collectionHandles[activeSize];
          apiPage += 1;
          const products = await fetchApiPage(handle, apiPage, token);
          if (token !== requestToken) return;
          if (!products.length) hasMoreApiPages = false;
          productCache.push(...products);
          if (products.length < 250) hasMoreApiPages = false;
          renderBrandFilters();
        }
        if (token !== requestToken) return;
        const added = renderNextChunk();
        if (!added && visibleCount >= productCache.length) hasMoreApiPages = false;
        updateLoadMoreVisibility();
      } catch (error) {
        console.error('[ScentAdda Tester Builder] View More failed:', error);
        loadText.textContent = 'Could not load more testers. Please try again.';
        loadText.hidden = false;
      } finally {
        isLoadingMore = false;
        if (!loadWrap.hidden) loadButton.disabled = false;
        loadButton.classList.remove('loading');
        if (loadText.textContent !== 'Could not load more testers. Please try again.') loadText.hidden = true;
      }
    }

    sizeButtons.forEach(button => button.addEventListener('click', () => {
      if (button.dataset.size !== activeSize) loadInitial(button.dataset.size);
    }));
    if (search) search.addEventListener('input', applyFilters);
    if (filters) filters.addEventListener('click', event => {
      const button = event.target.closest('button[data-brand]');
      if (!button) return;
      activeBrand = button.dataset.brand;
      filters.querySelectorAll('button').forEach(btn => {
        const active = btn === button;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      applyFilters();
    });
    loadButton.addEventListener('click', loadMoreProducts);

    addButton.addEventListener('click', async () => {
      if (addButton.disabled) return;
      const items = getCards().filter(card => selectedQty(card) > 0 && card.dataset.available !== 'false').map(card => ({ id: Number(card.dataset.variant), quantity: selectedQty(card) })).filter(item => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
      const total = items.reduce((sum, item) => sum + item.quantity, 0);
      if (total < 1) { updateSummary(); return; }
      addButton.disabled = true;
      addButton.classList.add('loading');
      addButton.textContent = 'Adding…';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add testers to cart.');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (error) {
        alert(error.message || 'Something went wrong. Please try again.');
        addButton.classList.remove('loading');
        updateSummary();
      }
    });

    loadInitial('2ml');
  }

  function boot() { document.querySelectorAll('.sa-tester').forEach(initTesterBuilder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', event => {
    const section = event.target.matches?.('.sa-tester') ? event.target : event.target.querySelector?.('.sa-tester');
    if (section) initTesterBuilder(section);
  });
})();
